import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
const api = axios.create({ baseURL: API });

export const TOKEN_KEY = "assortiq_token";
export const setToken = (t) => (t ? localStorage.setItem(TOKEN_KEY, t) : localStorage.removeItem(TOKEN_KEY));
export const getToken = () => localStorage.getItem(TOKEN_KEY);

api.interceptors.request.use((config) => {
  const t = getToken();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

// Auth + payments
export const apiRegister = (email, password, name) => api.post("/auth/register", { email, password, name }).then((r) => r.data);
export const apiLogin = (email, password) => api.post("/auth/login", { email, password }).then((r) => r.data);
export const apiGoogleSession = (sessionId) => api.post("/auth/google/session", {}, { headers: { "X-Session-ID": sessionId } }).then((r) => r.data);
export const apiMe = () => api.get("/auth/me").then((r) => r.data);
export const apiForgotPassword = (email, originUrl) => api.post("/auth/forgot-password", { email, origin_url: originUrl }).then((r) => r.data);
export const apiResetPassword = (token, newPassword) => api.post("/auth/reset-password", { token, new_password: newPassword }).then((r) => r.data);
export const apiChangePassword = (currentPassword, newPassword) => api.post("/auth/change-password", { current_password: currentPassword, new_password: newPassword }).then((r) => r.data);
export const apiBillingPortal = (originUrl) => api.post("/payments/portal", { origin_url: originUrl }).then((r) => r.data);
export const apiCheckout = (originUrl, tier = "grow", vertical = null) => api.post("/payments/checkout", { origin_url: originUrl, tier, ...(vertical ? { vertical } : {}) }).then((r) => r.data);
export const getVerticals = () => api.get("/verticals").then((r) => r.data);
export const setVertical = (vertical) => api.patch("/account/vertical", { vertical }).then((r) => r.data);
export const getPlans = () => api.get("/plans").then((r) => r.data.plans);
export const submitLead = (payload) => api.post("/leads", payload).then((r) => r.data);
export const apiPaymentStatus = (sessionId) => api.get(`/payments/status/${sessionId}`).then((r) => r.data);
export const getSampleData = () => api.get("/settings/sample-data").then((r) => r.data);
export const clearSampleData = (scope = "current") => api.post("/settings/clear-sample-data", { scope }).then((r) => r.data);
export const restoreSampleData = (scope = "current") => api.post("/settings/restore-sample-data", { scope }).then((r) => r.data);

export const getSummary = () => api.get("/dashboard/summary").then((r) => r.data);
export const getCategories = () => api.get("/categories").then((r) => r.data);
export const getSubcategories = (c) => api.get(`/categories/${encodeURIComponent(c)}/subcategories`).then((r) => r.data);
export const getProducts = (category, itemType) => api.get("/products", { params: { ...(category ? { category } : {}), ...(itemType ? { item_type: itemType } : {}) } }).then((r) => r.data);
export const getPareto = (category) => api.get("/analytics/pareto", { params: category ? { category } : {} }).then((r) => r.data);
export const getTrends = (category) => api.get("/analytics/trends", { params: category ? { category } : {} }).then((r) => r.data);
export const getHeatmap = (metric = "revenue") => api.get("/analytics/heatmap", { params: { metric } }).then((r) => r.data);
export const getStoreFormats = () => api.get("/stores/formats").then((r) => r.data);
export const getStores = () => api.get("/stores").then((r) => r.data);
export const getSuppliers = () => api.get("/suppliers").then((r) => r.data);
export const getCompetitors = (category) => api.get("/competitors", { params: category ? { category } : {} }).then((r) => r.data);
export const getPriceGaps = (limit = 6) => api.get("/competitors/price-gaps", { params: { limit } }).then((r) => r.data);
export const getPublicDemo = (vertical) => api.get(`/public/demo/${vertical}`).then((r) => r.data);
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const pollJob = async (jobId, { interval = 3000, maxMs = 240000 } = {}) => {
  const start = Date.now();
  while (Date.now() - start < maxMs) {
    await sleep(interval);
    const job = await api.get(`/jobs/${jobId}`).then((r) => r.data);
    if (job.status === "done") return job;
    if (job.status === "error") throw new Error(job.error || "AI request failed");
  }
  throw new Error("Timed out waiting for the AI result");
};

export const runOptimize = async (category) => {
  const { job_id } = await api.post("/optimize", { category }).then((r) => r.data);
  const job = await pollJob(job_id);
  return { result: job.result, category: job.category };
};
export const runWhatIf = async (scenario, category) => {
  const { job_id } = await api.post("/whatif", { scenario, category }).then((r) => r.data);
  const job = await pollJob(job_id);
  return { result: job.result };
};
export const getOptimizations = (category) => api.get("/optimizations", { params: category ? { category } : {} }).then((r) => r.data);

export const uploadData = (formData, replace = true, assumedMargin = null) =>
  api.post("/data/upload", formData, { params: { replace, ...(assumedMargin != null ? { assumed_margin: assumedMargin } : {}) }, headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
export const previewData = (formData) =>
  api.post("/data/preview", formData, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
export const getYoY = (category) => api.get("/analytics/yoy", { params: category ? { category } : {} }).then((r) => r.data);
export const uploadSuppliers = (formData) =>
  api.post("/suppliers/upload", formData, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
export const uploadCosts = (formData) =>
  api.post("/data/costs", formData, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);

export const downloadReport = async (kind, opts = {}) => {
  // opts: { category, cats: [], sections: [], metrics: [] }
  const params = {};
  if (opts.category) params.category = opts.category;
  if (opts.cats?.length) params.cats = opts.cats.join(",");
  if (opts.sections?.length) params.sections = opts.sections.join(",");
  if (opts.metrics?.length) params.metrics = opts.metrics.join(",");
  const res = await api.get(`/export/${kind}`, { params, responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([res.data]));
  const a = document.createElement("a");
  a.href = url;
  const ext = kind === "excel" ? "xlsx" : kind === "csv" ? "csv" : "pdf";
  a.download = kind === "csv" ? `assortiq_products.csv` : `assortiq_report.${ext}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

export const downloadImportTemplate = async () => {
  const res = await api.get("/data/template", { responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([res.data]));
  const a = document.createElement("a");
  a.href = url;
  const cd = res.headers?.["content-disposition"] || "";
  const m = cd.match(/filename=([^;]+)/);
  a.download = m ? m[1].trim() : "assortiq_import_template.csv";
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

// Scenario history (saved optimize / what-if runs)
export const getScenarios = (type) => api.get("/scenarios", { params: type ? { type } : {} }).then((r) => r.data);
export const getScenario = (id) => api.get(`/scenarios/${id}`).then((r) => r.data);
export const renameScenario = (id, name) => api.patch(`/scenarios/${id}`, { name }).then((r) => r.data);
export const deleteScenario = (id) => api.delete(`/scenarios/${id}`).then((r) => r.data);

// Email delivery & scheduled reports (Resend)
export const getEmailStatus = () => api.get("/reports/email/status").then((r) => r.data);
export const emailReport = (kind, category, to) =>
  api.post("/reports/email", { kind, category: category || null, to: to || null }).then((r) => r.data);
export const getSchedules = () => api.get("/reports/schedules").then((r) => r.data.schedules);
export const createSchedule = (payload) => api.post("/reports/schedules", payload).then((r) => r.data);
export const deleteSchedule = (id) => api.delete(`/reports/schedules/${id}`).then((r) => r.data);

export default api;
